import { shouldKeepAnalyticsTableComponentScope } from './AnalyticsTablePanel/sourceUtils';

const allPanelTypes = [
  { enum: ['components'], title: 'Components' },
  { enum: ['alarm'], title: 'Alarms' },
  { enum: ['event-timeline'], title: 'Event Timeline' },
  { enum: ['files'], title: 'Files' },
  { enum: ['text'], title: 'Text' },
  { enum: ['tfr-viewer'], title: 'TFR Viewer' },
  { enum: ['visualizations'], title: 'Visualizations' },
  { enum: ['health'], title: 'Status' },
  { enum: ['component-properties'], title: 'Component Attributes' },
  { enum: ['analytics-table'], title: 'Table' },
];

const widePanelTypes = new Set(['alarm', 'event-timeline']);

const fitContentOnlyPanelTypes = new Set([
  'visualizations',
  'event-timeline',
  'files',
  'tfr-viewer',
  'health',
  'component-properties',
]);
// These panel types may only appear once on a page, counting both viewer and section panels.
// This is an application limitation: the components panel breaks if more than one exists on a page.
const uniquePerPagePanelTypes = new Set(['components']);
const PREFERRED_DEFAULT_PANEL_TYPE = 'components';

const getFilteredPanelTypes = (excludeTypes = []) => {
  return allPanelTypes.filter((panelType) => !excludeTypes.includes(panelType.enum[0]));
};

const getDefaultPanelType = (panelTypes = allPanelTypes) => {
  const preferredPanelType = panelTypes.find(
    ({ enum: [panelType] }) => panelType === PREFERRED_DEFAULT_PANEL_TYPE
  );

  return preferredPanelType?.enum?.[0] || panelTypes[0]?.enum?.[0];
};

const panelTypes = allPanelTypes;

const panelSchema = {
  type: 'object',
  required: ['type', 'title'],
  properties: {
    title: {
      type: 'string',
      title: 'Panel title',
      minLength: 1,
    },
    displayTitle: {
      type: 'boolean',
      title: 'Display title',
      default: false,
    },
    allowFullScreen: {
      type: 'boolean',
      default: false,
      title: 'Allow full screen',
    },
    type: {
      type: 'string',
      title: 'Panel type',
      default: PREFERRED_DEFAULT_PANEL_TYPE,
      oneOf: panelTypes,
    },
  },
};

const nonComponentScopedPanels = new Set([
  'components',
  'text',
  'tfr-viewer',
  'health',
  'component-properties',
]);

const panelScopedPanels = new Set(['visualizations']);
const componentTypeFilterExcludedPanels = new Set(['alarm']);

const buildComponentScopeSchema = (panelType) => {
  const includeActiveComponentFilter = !componentTypeFilterExcludedPanels.has(panelType);

  const scopeProperties = {
    selectedScope: {
      title: 'Selected Scope',
      type: 'string',
      default: 'all',
      oneOf: scopeOptions,
    },
  };

  if (includeActiveComponentFilter) {
    scopeProperties.activeComponentTypeFilter = {
      type: 'string',
      default: 'any',
      title: 'Only components of type',
      oneOf: [
        { enum: ['any'], title: 'Any' },
        { enum: ['site'], title: 'Site' },
        { enum: ['component'], title: 'Equipment' },
        { enum: ['group'], title: 'Group' },
        { enum: ['virtual'], title: 'Virtual' },
      ],
    };
  }

  return {
    type: 'object',
    title: 'Component scope',
    description: 'Set panel content based on components',
    properties: scopeProperties,
  };
};

const scopeOptions = [
  { enum: ['all'], title: 'All components' }, // all components
  { enum: ['active_all'], title: 'Active component, else all' }, // active component only, or all components if none active
  { enum: ['active_none'], title: 'Active component, else none' }, // active component only, or no components if none active
  { enum: ['active_children_all'], title: 'Active with children, else all' }, // active component and children, or all components if none active
  { enum: ['active_children_none'], title: 'Active with children, else none' }, // active component and children, or no components if none active
];
const COMPONENT_SCOPES = scopeOptions.map(({ enum: [scope] }) => scope);

const getExtendedPanelSchema = (
  extendedPanels,
  components = [],
  filteredPanelTypes = panelTypes,
  sectionContext = {} // Section context for dynamic filtering
) => {
  const { columnCount = 1, sectionHeight = 'fit-content', columnIndex = 0 } = sectionContext;

  // Filter out incompatible panel types based on section constraints
  let contextFilteredPanelTypes = filteredPanelTypes;

  // Wide panels (Alarm, Event Timeline) only allowed in:
  // - Single column sections (columnCount === 1)
  // - First column of 2-column sections (columnCount === 2 && columnIndex === 0, which is 66.66% width)
  // For 3+ columns, all columns are narrow (33.33% or less), so wide panels are excluded
  const isNarrowColumn = columnCount > 2 || (columnCount === 2 && columnIndex === 1);

  if (isNarrowColumn) {
    contextFilteredPanelTypes = contextFilteredPanelTypes.filter(
      (panelType) => !widePanelTypes.has(panelType.enum[0])
    );
  }

  // If section height is not fit-content, exclude fit-content-only panels
  if (sectionHeight && sectionHeight !== 'fit-content') {
    contextFilteredPanelTypes = contextFilteredPanelTypes.filter(
      (panelType) => !fitContentOnlyPanelTypes.has(panelType.enum[0])
    );
  }

  const componentOptions = components?.length
    ? components.map((component) => ({
        enum: [component.id],
        title: component.name,
      }))
    : [];

  return {
    ...panelSchema,
    properties: {
      ...panelSchema.properties,
      type: {
        ...panelSchema.properties.type,
        oneOf: contextFilteredPanelTypes,
      },
    },
    dependencies: {
      type: {
        oneOf: Object.keys(extendedPanels).map((panelType) => {
          if (nonComponentScopedPanels.has(panelType)) {
            return {
              properties: {
                type: { enum: [panelType] },
                ...extendedPanels[panelType],
              },
            };
          }

          const baseProperties = {
            type: { enum: [panelType] },
          };

          if (panelScopedPanels.has(panelType)) {
            const panelScopeProperties = {
              selectedScope: {
                title: 'Selected Scope',
                description: 'Optionally display the panel',
                type: 'string',
                default: 'all',
                oneOf: [
                  { enum: ['all'], title: 'All components' },
                  { enum: ['specific'], title: 'Specific component' },
                ],
              },
            };

            baseProperties.panelScope = {
              type: 'object',
              title: 'Panel Scope',
              description: 'Set panel visibility based on components',
              properties: panelScopeProperties,
              required: ['selectedScope'],
            };

            if (componentOptions.length > 0) {
              baseProperties.panelScope.dependencies = {
                selectedScope: {
                  oneOf: [
                    {
                      properties: {
                        selectedScope: { enum: ['specific'] },
                        selectedComponent: {
                          type: 'string',
                          title: 'Selected Component',
                          description: 'Choose which component the panel will be bound to',
                          default: componentOptions[0].enum[0],
                          anyOf: componentOptions,
                        },
                      },
                      required: ['selectedComponent'],
                    },
                    {
                      properties: {
                        selectedScope: { enum: ['all'] },
                      },
                    },
                  ],
                },
              };
            }
          }

          const componentScope = buildComponentScopeSchema(panelType);

          const {
            options: extendedOptions,
            dependencies: extendedDependencies,
            ...extendedTopLevel
          } = extendedPanels[panelType];

          if (panelType === 'analytics-table') {
            const analyticsTableDependency = extendedDependencies?.tableType;
            const analyticsTableOptions = analyticsTableDependency?.oneOf?.map(
              (tableTypeOption) => {
                const selectedTableType = tableTypeOption.properties?.tableType?.enum?.[0];

                if (!shouldKeepAnalyticsTableComponentScope(selectedTableType)) {
                  return tableTypeOption;
                }

                return {
                  ...tableTypeOption,
                  properties: {
                    ...tableTypeOption.properties,
                    componentScope,
                  },
                };
              }
            );

            return {
              properties: {
                type: { enum: [panelType] },
                ...extendedTopLevel,
                ...(baseProperties.panelScope && { panelScope: baseProperties.panelScope }),
                ...(extendedOptions && { options: extendedOptions }),
              },
              ...(extendedDependencies && {
                dependencies: {
                  ...extendedDependencies,
                  ...(analyticsTableDependency && analyticsTableOptions
                    ? {
                        tableType: {
                          ...analyticsTableDependency,
                          oneOf: analyticsTableOptions,
                        },
                      }
                    : {}),
                },
              }),
            };
          }

          return {
            properties: {
              type: { enum: [panelType] },
              ...extendedTopLevel,
              ...(baseProperties.panelScope && { panelScope: baseProperties.panelScope }),
              componentScope,
              ...(extendedOptions && { options: extendedOptions }),
            },
            ...(extendedDependencies && { dependencies: extendedDependencies }),
          };
        }),
      },
    },
  };
};

export {
  panelSchema,
  getExtendedPanelSchema,
  COMPONENT_SCOPES,
  panelScopedPanels,
  getFilteredPanelTypes,
  uniquePerPagePanelTypes,
  widePanelTypes,
  fitContentOnlyPanelTypes,
  getDefaultPanelType,
};
